import React, {Fragment, useContext} from 'react';
import {graphql} from 'react-apollo';
import PropTypes from 'prop-types';

import {LoadingOverlay} from '@homeaway/react-loading-overlay';
import {CancellationPolicyQuery} from './CancellationPolicyQuery';

import StayContext from '../../../../../../context/StayContext';
import translate from '../../../../../../../utils/translations';

import './CancellationPolicyTooltip.less';

const isPolicyTypeChanged = (policyType1, policyType2) => {
    policyType1 = policyType1.replace(/_/g, '').toLowerCase();
    policyType2 = policyType2.replace(/_/g, '').toLowerCase();

    return policyType1 !== policyType2;
};

const CancellationPolicyTooltip = (props) => {
    const {
        data: {error, loading, houseRules},
        policyType: bookingPolicyType,
        translations,
    } = props;

    const {
        stay: {
            unit: {ownerManaged},
        },
    } = useContext(StayContext);

    const title = (
        <div className="CancellationPolicyTooltip__title">
            {translate(
                translations,
                'trav-dash/trav-home',
                'messages',
                'modifyBooking.request.policyTooltipTitle'
            )}
        </div>
    );

    const contactOwnerOrManager = (
        <div className="CancellationPolicyTooltip__text">
            {ownerManaged
                ? translate(
                      translations,
                      'trav-dash/trav-home',
                      'messages',
                      'modifyBooking.request.noPolicyText.contactOwner'
                  )
                : translate(
                      translations,
                      'trav-dash/trav-home',
                      'messages',
                      'modifyBooking.request.noPolicyText.contactPM'
                  )}
        </div>
    );

    const errorMessage = (
        <Fragment>
            {title}
            {contactOwnerOrManager}
        </Fragment>
    );

    if (loading) {
        return <LoadingOverlay />;
    }

    if (error) {
        return errorMessage;
    }

    const {
        cancellationPolicyPeriods,
        nonRefundableFeeLabel,
        policyType: listingPolicyType,
    } = houseRules?.lodgingCancellationPolicy || {};
    const policyCount = (cancellationPolicyPeriods || []).length;
    const hasCancellationPolicyPeriods =
        !!policyCount || !!nonRefundableFeeLabel;
    const hasMultiplePeriods = policyCount > 1;

    // Contact owner/property manager if policy type (eg. Relaxed) is different.
    if (
        bookingPolicyType &&
        listingPolicyType &&
        isPolicyTypeChanged(bookingPolicyType, listingPolicyType)
    ) {
        return errorMessage;
    }

    return (
        <Fragment>
            {title}
            {!hasCancellationPolicyPeriods && contactOwnerOrManager}
            {nonRefundableFeeLabel && (
                <div className="CancellationPolicyTooltip__text">
                    {nonRefundableFeeLabel}
                </div>
            )}
            {cancellationPolicyPeriods &&
                cancellationPolicyPeriods.map((policy, index) => {
                    // Remove html tags from cancellation period label
                    return (
                        <div
                            key={`policy-${index + 1}`}
                            className="CancellationPolicyTooltip__text"
                        >
                            {policy.label.replace(/(<([^>]+)>)/g, '')}
                        </div>
                    );
                })}
            {hasMultiplePeriods && (
                <div className="CancellationPolicyTooltip__text">
                    {translate(
                        translations,
                        'traveler/ces',
                        'messages',
                        'cancellation.cko.addendum'
                    )}
                </div>
            )}
        </Fragment>
    );
};

export default graphql(CancellationPolicyQuery, {
    options: (ownProps) => {
        return {
            variables: {
                arrivalDate: ownProps.arrivalDate,
                isIpmNonGuaranteedPricing: ownProps.isIpmNonGuaranteedPricing,
                unitUrl: ownProps.unitUrl,
            },
            errorPolicy: 'all',
        };
    },
})(CancellationPolicyTooltip);

CancellationPolicyTooltip.propTypes = {
    arrivalDate: PropTypes.string,
    isIpmNonGuaranteedPricing: PropTypes.bool,
    policyType: PropTypes.string,
    translations: PropTypes.shape({}),
    unitUrl: PropTypes.string,
};
